import { useNavigate, useParams } from "react-router-dom";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, Play, CheckCircle2, Lock } from "lucide-react";
import { AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useDriverProfile } from "@/hooks/useDriverProfile";
import { useDriverAssignmentDetail } from "@/hooks/useDriverAssignmentDetail";
import { useDriverLegStatusUpdate } from "@/hooks/useDriverLegStatusUpdate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { HoldToConfirmButton } from "@/components/driver/HoldToConfirmButton";
import { departureChecklistMissing } from "@/lib/driver-departure-readiness";
import { ReadinessChecklist } from "@/components/driver/ReadinessChecklist";
import { DriverDocsChecklist } from "@/components/driver/DriverDocsChecklist";
import { ManualDepartureSheet } from "@/components/driver/ManualDepartureSheet";
import { EnvironmentSwitcher, useEnvironmentBorderClass } from "@/components/layout/EnvironmentSwitcher";
import type { OrderWithRelations } from "@/components/schedule/types";
import { isLateDeparture, isLateArrival, formatTime, formatTimeWithTz } from "@/lib/order-time";

export default function DriverAssignmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { driver } = useDriverProfile();
  const { leg, siblings, isLoading } = useDriverAssignmentDetail(id);
  const envBorderClass = useEnvironmentBorderClass();
  const { tenantId } = useTenant();

  const trailerIdText = (leg as { trailer_id?: string | null } | null)?.trailer_id ?? null;
  const { data: trailerServiceInfo } = useQuery({
    queryKey: ["driver-detail-trailer-needs-service", trailerIdText, tenantId],
    enabled: !!trailerIdText && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("needs_service, needs_service_reason")
        .eq("trailer_id", trailerIdText!)
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      return data as { needs_service: boolean | null; needs_service_reason: string | null } | null;
    },
  });

  const isMultiLeg = siblings.length > 1;
  const legSeq = (leg as any)?.leg_sequence as number | null | undefined;

  // Sequential gating: lock this stop's actions until every earlier sibling
  // is delivered. Per-leg `driver_status` is already overlaid onto `status`
  // by `hydrateLegRelations`.
  const priorIncomplete = isMultiLeg && legSeq != null
    ? siblings.filter((s) => {
        const seq = (s as any).leg_sequence as number | null | undefined;
        return seq != null && seq < legSeq && s.status !== "delivered";
      })
    : [];
  const isLockedByPriorLeg = priorIncomplete.length > 0;
  const blockingSeq = priorIncomplete[0]
    ? ((priorIncomplete[0] as any).leg_sequence as number | null)
    : null;

  const headerEl = (
    <header className={`sticky top-0 z-10 flex h-14 items-center gap-2 border-b bg-card px-3 ${envBorderClass}`}>
      <Button variant="ghost" size="icon" onClick={() => navigate("/my-assignments")} className="shrink-0">
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <div className="flex-1" />
      {isMultiLeg && legSeq != null && (
        <Badge className="bg-primary text-primary-foreground hover:bg-primary font-semibold">
          Stop {legSeq}
        </Badge>
      )}
      <EnvironmentSwitcher variant="header" />
    </header>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {headerEl}
        <main className="p-4 text-center text-sm text-muted-foreground">Loading…</main>
      </div>
    );
  }

  if (!leg) {
    return (
      <div className="min-h-screen bg-background">
        {headerEl}
        <main className="p-4">
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Assignment not found.
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {headerEl}
      <main className="p-4 pb-24">
        <div className="max-w-lg mx-auto space-y-5">
          {/* Title */}
          <h1 className="text-2xl font-bold text-foreground truncate">
            {leg.unique_id ?? "Assignment"}
          </h1>

          {/* Status row */}
          <div className="flex items-center gap-2 flex-wrap">
            <OrderStatusBadge
              status={leg.status}
              late={isLateDeparture(leg as any) || isLateArrival(leg as any)}
            />
            {(leg as any).trip_type && (
              <Badge variant="secondary" className="font-normal">
                {(leg as any).trip_type}
              </Badge>
            )}
          </div>

          {trailerServiceInfo?.needs_service && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-destructive">
                    Trailer needs service
                  </div>
                  {trailerServiceInfo.needs_service_reason && (
                    <div className="text-sm text-foreground whitespace-pre-wrap mt-0.5">
                      {trailerServiceInfo.needs_service_reason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Prominent Truck ID / Trailer ID tiles */}
          <div className="grid grid-cols-2 rounded-md border bg-card divide-x">
            <IdTile label="TRUCK ID" value={leg.assigned_tractor?.name ?? null} placeholder="{Truck ID}" />
            <IdTile label="TRAILER ID" value={leg.trailer_id ?? null} placeholder="{Trailer ID}" />
          </div>

          <SingleLegBody
            leg={leg}
            driverName={driver?.name}
            driverId={driver?.id}
            isLockedByPriorLeg={isLockedByPriorLeg}
            blockingSeq={blockingSeq}
          />
        </div>
      </main>
    </div>
  );
}

function IdTile({ label, value, placeholder }: { label: string; value: string | null; placeholder: string }) {
  return (
    <div className="px-3 py-2">
      <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
        {label}
      </div>
      <div className={`font-mono text-base ${value ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
        {value ?? placeholder}
      </div>
    </div>
  );
}

function SingleLegBody({
  leg,
  driverName,
  driverId,
  isLockedByPriorLeg,
  blockingSeq,
}: {
  leg: OrderWithRelations;
  driverName?: string;
  driverId?: string;
  isLockedByPriorLeg?: boolean;
  blockingSeq?: number | null;
}) {
  const viewerTz = useViewerTimezone();
  const { updateOrderStatus, isUpdating } = useDriverLegStatusUpdate(leg, driverName);

  // Resolve location short codes by full street address (city alone is
  // ambiguous — multiple locations can share a city like Palmer, TX).
  const addresses = [leg.ship_from_street_address, leg.ship_to_street_address].filter(
    Boolean,
  ) as string[];
  const { data: shortCodeByAddress = {} } = useQuery({
    queryKey: ["location-short-codes-by-address", ...addresses],
    queryFn: async () => {
      if (!addresses.length) return {} as Record<string, string>;
      const { data } = await supabase
        .from("locations")
        .select("street_address, short_code, track_in_yms")
        .eq("archived", false)
        .in("street_address", addresses);
      const map: Record<string, string> = {};
      data?.forEach((l: any) => {
        if (l.street_address) map[l.street_address.toLowerCase()] = l.short_code;
      });
      return map;
    },
  });
  const fromShortCode = shortCodeByAddress[(leg.ship_from_street_address ?? "").toLowerCase()];
  const toShortCode = shortCodeByAddress[(leg.ship_to_street_address ?? "").toLowerCase()];

  // Verify Trailer step applies when the PICKUP is not YMS-tracked (DTP-110609).
  // A YMS-tracked pickup already records the trailer via AYMS check-out.
  // No default: guessing `true` while loading made the whole readiness
  // checklist (Verify Trailer, photos) flash on YMS stops, then vanish.
  const { data: verifyRequired, isPending: verifyPending } = useQuery({
    queryKey: [
      "driver-verify-required",
      leg.ship_from_street_address,
      (leg as any).ship_from_location_id,
    ],
    queryFn: async () => {
      // Pickup decides: a YMS-tracked pickup already records the trailer at
      // check-out; any other pickup needs the driver to verify for this leg.
      const ids = [(leg as any).ship_from_location_id].filter(Boolean) as string[];
      const addrs = [leg.ship_from_street_address].filter(Boolean) as string[];
      const sb = supabase as any;
      let anyYms = false;
      if (ids.length) {
        const { data } = await sb
          .from("locations")
          .select("track_in_yms")
          .in("id", ids);
        if (data?.some((l: any) => l.track_in_yms)) anyYms = true;
      }
      if (!anyYms && addrs.length) {
        const { data } = await sb
          .from("locations")
          .select("track_in_yms")
          .eq("archived", false)
          .in("street_address", addrs);
        if (data?.some((l: any) => l.track_in_yms)) anyYms = true;
      }
      return !anyYms;
    },
  });

  const dateFmt = "MMM d";
  const timeFmt = "h:mm a";
  const fullFmt = `${dateFmt}, ${timeFmt}`;
  const buildWindow = (
    startUtc: string | null | undefined,
    endUtc: string | null | undefined,
    fmtStart: (utc: string | null | undefined, f: string) => string,
    fmtEndWithTz: (utc: string | null | undefined, f: string) => string,
  ): string => {
    if (!startUtc) return "";
    const startFull = fmtStart(startUtc, fullFmt);
    if (!endUtc || endUtc === startUtc) return fmtEndWithTz(startUtc, fullFmt);
    const startDate = fmtStart(startUtc, dateFmt);
    const endDate = fmtStart(endUtc, dateFmt);
    const sameDay = startDate === endDate;
    const endStr = sameDay
      ? fmtEndWithTz(endUtc, timeFmt)
      : fmtEndWithTz(endUtc, fullFmt);
    return `${startFull} – ${endStr}`;
  };
  const pickupWindow = buildWindow(
    leg.ready_time,
    (leg as any).ready_time_end,
    (u, f) => formatTime(u, viewerTz, f),
    (u, f) => formatTimeWithTz(u, viewerTz, f),
  );
  const deliveryWindow = buildWindow(
    leg.requested_delivery_time,
    (leg as any).requested_delivery_time_end,
    (u, f) => formatTime(u, viewerTz, f),
    (u, f) => formatTimeWithTz(u, viewerTz, f),
  );

  // `leg.id` from the `orders_with_legs` view is a leg_id; notes/BOLs live on
  // the parent order row. Never fall back to the leg id: querying `orders` with
  // a leg UUID returns zero rows and surfaces as an opaque note/BOL failure.
  const orderId = ((leg as any).parent_order_id as string | undefined) ?? null;
  if (!orderId) {
    console.error("[driver-assignment] leg is missing parent_order_id", {
      legId: leg.id,
      legSequence: (leg as any).leg_sequence ?? null,
    });
  }

  const [checklistState, setChecklistState] = useState<{
    verified: boolean;
    trailerPhotoLeftCount: number;
    trailerPhotoRightCount: number;
    licensePlatePhotoCount: number;
    bolCount: number;
    bolRequired: boolean;
    allRequiredComplete: boolean;
    needsService: boolean;
  }>({
    verified: false,
    trailerPhotoLeftCount: 0,
    trailerPhotoRightCount: 0,
    licensePlatePhotoCount: 0,
    bolCount: 0,
    bolRequired: true,
    allRequiredComplete: false,
    needsService: false,
  });

  return (
    <>
      {/* Pickup / Delivery */}
      <div className="space-y-4">
        <div className="flex gap-3">
          <div className="flex flex-col items-center self-stretch shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <ArrowUpRight className="h-4 w-4" />
            </div>
            <div className="w-px flex-1 bg-border mt-1 -mb-4 border-primary border py-0 my-0" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
              PICKUP{pickupWindow ? ` BY ${pickupWindow}` : ""}
            </div>
            {fromShortCode && (
              <div className="font-bold text-base">{fromShortCode}</div>
            )}
            {leg.ship_from_street_address && (
              <div className="font-semibold">{leg.ship_from_street_address}</div>
            )}
            <div className="text-sm text-muted-foreground">
              {leg.ship_from_city}
              {leg.ship_from_province_state ? `, ${leg.ship_from_province_state}` : ""}
              {leg.ship_from_postal_zip_code ? ` ${leg.ship_from_postal_zip_code}` : ""}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <ArrowDownRight className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
              DELIVER BY{deliveryWindow ? ` ${deliveryWindow}` : ""}
            </div>
            {toShortCode && (
              <div className="font-bold text-base">{toShortCode}</div>
            )}
            {leg.ship_to_street_address && (
              <div className="font-semibold">{leg.ship_to_street_address}</div>
            )}
            <div className="text-sm text-muted-foreground">
              {leg.ship_to_city}
              {leg.ship_to_province_state ? `, ${leg.ship_to_province_state}` : ""}
              {leg.ship_to_postal_zip_code ? ` ${leg.ship_to_postal_zip_code}` : ""}
            </div>
          </div>
        </div>
      </div>

      {/*
        Readiness checklist (Verify + trailer/plate photos + BOL + Notes) —
        only rendered on non-YMS legs when the feature flag is on. YMS legs
        (and readiness-off environments) fall back to the compact BOL/Notes
        checklist below, which reuses the same row style.
      */}
      {!orderId ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-muted-foreground">
          Documents and notes aren't available for this stop because its parent
          order couldn't be resolved. Please refresh, or contact dispatch if it
          keeps happening.
        </div>
      ) : verifyPending && !isLockedByPriorLeg ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading checklist">
          <div className="h-12 rounded-md bg-muted animate-pulse" />
          <div className="h-12 rounded-md bg-muted animate-pulse" />
        </div>
      ) : verifyRequired && !isLockedByPriorLeg ? (
        <ReadinessChecklist
          legId={leg.id}
          orderId={orderId}
          orderUniqueId={leg.unique_id}
          trailerId={leg.trailer_id ?? null}
          driverName={driverName}
          verifyRequired={verifyRequired}
          onReadinessChange={setChecklistState}
        />
      ) : (
        <DriverDocsChecklist
          orderId={orderId}
          orderUniqueId={leg.unique_id}
          driverName={driverName}
        />
      )}

      {/* Sticky status action */}
      {isLockedByPriorLeg ? (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-3 z-20">
          <div className="max-w-lg mx-auto">
            <div className="w-full flex items-center justify-center gap-2 rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground">
              <Lock className="h-4 w-4" />
              Complete Stop {blockingSeq ?? ""} first
            </div>
          </div>
        </div>
      ) : !verifyPending && (leg.status === "booked" || leg.status === "confirmed" || leg.status === "in_transit") && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-3 z-20">
          <div className="max-w-lg mx-auto">
            {(() => {
              const tripType = (leg as any).trip_type as string | null | undefined;
              const isManual = tripType === "Manual";
              const isBooked = leg.status === "booked";
              const isInTransit = leg.status === "in_transit";
              // Departure (booked/confirmed → in_transit) is gated on the
              // checklist; arrival ("I've arrived") is not — by then BOL
              // and photos are already captured.
              const missing: string[] =
                verifyRequired && !isInTransit
                  ? departureChecklistMissing({ ...checklistState, verifyRequired: true })
                  : [];
              const nonManualDepart = !isInTransit && !isManual;
              // Non-manual trips no longer render a disabled amber CTA —
              // they render an enabled secondary button that opens an
              // explainer sheet (see ManualDepartureSheet).
              if (nonManualDepart) {
                return <ManualDepartureSheet tripType={tripType} />;
              }
              const disabledReason = isBooked
                ? "This order hasn't been confirmed by dispatch yet, so it can't be marked in transit."
                : checklistState.needsService && !isInTransit
                  ? "Trailer is marked as needing service. Contact dispatch before departing."
                  : missing.length > 0
                    ? `Complete the checklist first: ${missing.join(", ")}.`
                    : null;
              const bookedManualDepart = !isInTransit && isManual && isBooked;
              const label = isInTransit
                ? "I've Arrived"
                : "Ready to Depart";
              const secondaryText = bookedManualDepart
                ? `This order hasn't been confirmed by dispatch yet, so it's not ready to depart and marked "In Transit".`
                : null;
              const icon = isInTransit
                ? <CheckCircle2 className="h-5 w-5 mr-2" />
                : <Play className="h-5 w-5 mr-2" />;
              const className = isInTransit
                ? "w-full bg-green-600 hover:bg-green-700 text-white"
                : "w-full bg-amber-500 hover:bg-amber-600 text-white";
              const onConfirm = () =>
                updateOrderStatus(isInTransit ? "delivered" : "in_transit");
              const button = (
                <HoldToConfirmButton
                  onConfirm={onConfirm}
                  loading={isUpdating}
                  disabled={disabledReason !== null}
                  idleLabel={label}
                  icon={icon}
                  className={className}
                />
              );
              const secondaryNode = secondaryText ? (
                <p className="mt-2 text-xs text-muted-foreground text-center">
                  {secondaryText}
                </p>
              ) : null;
              if (!disabledReason) return <>{button}{secondaryNode}</>;
              if (secondaryText) return <>{button}{secondaryNode}</>;
              return (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0} className="block w-full">{button}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-center">
                      {disabledReason}
                    </TooltipContent>
                  </Tooltip>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}